import { FeeServiceFormInput } from '@/pages/Fleet/Businesses/models';
import { feeServiceSchema } from '@/pages/Fleet/Businesses/models/FeeService';
import { fleetPageBreadcrumbs } from '../../../config/breadcrumbs';
import { NavigateLink } from '@/components/navigate/NavigateLinks';
import { NavigateLinks, BackToModuleButton } from '@/components';
import { useFeeService } from '@/pages/Fleet/Businesses/hooks';
import { useServiceFormOptions, useUpdateFeeService } from '../../hooks';
import { useNavigate, useParams } from 'react-router-dom';
import ServiceForm from '../../components/ServiceForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';

const EditService = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  if (!serviceId) throw new Error('El id del servicio es requerido');

  const navigate = useNavigate();
  const { feeService, isLoading } = useFeeService({ feeServiceId: serviceId });
  const updateService = useUpdateFeeService({
    feeServiceId: serviceId,
    businessId: feeService?.commercialBusiness?.id,
    onSuccess: () => navigate(`/fleet/services/${serviceId}`),
  });
  
  const {
    vehicleTypesOptions,
    serviceTypesOptions,
    vehicleTypesLoading,
    serviceTypesLoading,
  } = useServiceFormOptions();

  const breadcrumbs = useMemo<NavigateLink[]>(() => {
    const base = fleetPageBreadcrumbs(navigate);
    return [
      ...base,
      { label: 'Servicios', onClick: () => navigate(`/fleet/services/${serviceId}`) },
    ];
  }, [navigate, serviceId]);

  const { handleSubmit, control, formState, setValue, trigger } =
    useForm<FeeServiceFormInput>({
      resolver: zodResolver(feeServiceSchema),
      mode: 'onChange',
      shouldFocusError: false,
      defaultValues: {
        vehicleTypeId: '',
        serviceTypeId: '',
        fee: '',
        min: '',
        validFrom: new Date(),
        validTo: new Date(),
      },
    });

  useEffect(() => {
    if (!isLoading && feeService?.id && !vehicleTypesLoading && !serviceTypesLoading) {
      setValue('vehicleTypeId', feeService.vehicleType.id);
      setValue('serviceTypeId', feeService.serviceType.id);
      setValue('fee', feeService.fee.toString());
      setValue('min', feeService.min.toString());
      setValue('validFrom', new Date(feeService.validFrom));
      setValue('validTo', new Date(feeService.validTo));
      trigger();
    }
  }, [isLoading, feeService, vehicleTypesLoading, serviceTypesLoading, setValue, trigger]);

  const actualPage = feeService?.name ?? 'Editar servicio';

  return (
    <section className="flex h-full w-full flex-col gap-[25px]">
      <NavigateLinks actualPage={actualPage} breadcrumbs={breadcrumbs} />
      <div className="flex justify-end gap-5">
        <BackToModuleButton breadcrumbs={breadcrumbs} />
      </div>
      <div className="w-full flex rounded-[5px] shadow-[0_0_4px_rgba(0,0,0,0.1)]">
        {isLoading ? (
          <p className="p-[50px] text-gray-600">Cargando...</p>
        ) : (
          <ServiceForm<FeeServiceFormInput>
            mode="edit"
            control={control}
            handleSubmit={handleSubmit(updateService)}
            vehicleTypesOptions={vehicleTypesOptions}
            serviceTypesOptions={serviceTypesOptions}
            vehicleTypesLoading={vehicleTypesLoading}
            serviceTypesLoading={serviceTypesLoading}
            title="Información del servicio"
            onCancel={() => navigate(`/fleet/services/${serviceId}`)}
            submitLabel="Actualizar servicio"
            isSubmitting={formState.isSubmitting}
            submitDisabled={!formState.isValid || !formState.isDirty}
          />
        )}
      </div>
    </section>
  );
};

export default EditService;
